import { strapiApiSlice } from "../strapiApi";
import { strapiMediaUrl } from "../../strapi/media";

export const strapiTag = "strapi";

const strapiApiWithTags = strapiApiSlice.enhanceEndpoints({
	addTagTypes: [strapiTag],
});

function getHomePageField(response, field) {
	// Support both response shapes:
	// - Strapi default REST: response.data.attributes[field]
	// - Custom/transformed API: response.data[field]
	const data = response?.data;
	if (!data) return undefined;
	if (Object.prototype.hasOwnProperty.call(data, field)) return data[field];
	const attrs = data?.attributes;
	if (attrs && Object.prototype.hasOwnProperty.call(attrs, field)) return attrs[field];
	return undefined;
}

function buildQueryString(params) {
	const qs = new URLSearchParams();
	Object.entries(params).forEach(([k, v]) => {
		if (v == null) return;
		qs.set(k, String(v));
	});
	return qs.toString();
}

export const strapiHomeApi = strapiApiWithTags.injectEndpoints({
	endpoints: (builder) => ({
		getHomePagePartners: builder.query({
			query: () => {
				const qs = buildQueryString({
					"populate[trustedPartners][populate]": "logo",
				});
				return { url: `/home-page?${qs}` };
			},
			transformResponse: (response) => {
				const partnersRaw = getHomePageField(response, "trustedPartners");
				const partners = Array.isArray(partnersRaw) ? partnersRaw : [];

				return partners
					.map((p) => {
						const logoUrl =
							p?.logo?.url || p?.logo?.data?.attributes?.url || p?.logo?.data?.attributes?.formats?.thumbnail?.url || p?.logo?.data?.attributes?.formats?.small?.url;

						const src = strapiMediaUrl(logoUrl);
						if (!src) return null;

						const href = typeof p?.url === "string" && p.url.trim() ? p.url.trim() : undefined;
						return { src, href };
					})
					.filter(Boolean);
			},
			providesTags: [strapiTag],
		}),
		getHomePageAwards: builder.query({
			query: () => {
				const qs = buildQueryString({
					"populate[awards][populate]": "logo",
				});
				return { url: `/home-page?${qs}` };
			},
			transformResponse: (response) => {
				const awardsRaw = getHomePageField(response, "awards");
				const awards = Array.isArray(awardsRaw) ? awardsRaw : [];

				return awards
					.map((a) => {
						const name = typeof a?.name === "string" ? a.name : "";
						const logoUrl =
							a?.logo?.url || a?.logo?.data?.attributes?.url || a?.logo?.data?.attributes?.formats?.thumbnail?.url || a?.logo?.data?.attributes?.formats?.small?.url;
						const src = strapiMediaUrl(logoUrl);
						if (!src) return null;
						return { src, name };
					})
					.filter(Boolean);
			},
			providesTags: [strapiTag],
		}),
		getHomePageSponsorshipCommunity: builder.query({
			query: () => {
				const qs = buildQueryString({
					"populate[sponsorshipCommunity][populate]": "logo",
				});
				return { url: `/home-page?${qs}` };
			},
			transformResponse: (response) => {
				const raw = getHomePageField(response, "sponsorshipCommunity");
				const items = Array.isArray(raw) ? raw : [];

				return items
					.map((i) => {
						const name = typeof i?.name === "string" ? i.name : "";
						const logoUrl =
							i?.logo?.url || i?.logo?.data?.attributes?.url || i?.logo?.data?.attributes?.formats?.thumbnail?.url || i?.logo?.data?.attributes?.formats?.small?.url;
						const src = strapiMediaUrl(logoUrl);
						if (!src) return null;
						return { src, name };
					})
					.filter(Boolean);
			},
			providesTags: [strapiTag],
		}),
		getTestimonials: builder.query({
			query: () => ({ url: "/testimonials?sort=id:desc" }),
			transformResponse: (response) => {
				// Support both:
				// - Strapi default REST list: { data: [{ id, attributes: { name, body, rate } }] }
				// - Custom list: { data: [{ name, text/body, rating/rate }] }
				const raw = response?.data;
				const list = Array.isArray(raw) ? raw : [];

				return list
					.map((item) => {
						const attrs = item?.attributes ?? item;
						const name = typeof attrs?.name === "string" ? attrs.name : "";
						const text = typeof attrs?.body === "string" ? attrs.body : typeof attrs?.text === "string" ? attrs.text : "";
						const ratingRaw = attrs?.rate ?? attrs?.rating;
						const rating = ratingRaw == null ? undefined : Number(ratingRaw);

						if (!name || !text) return null;
						return { name, text, rating: Number.isFinite(rating) ? rating : undefined };
					})
					.filter(Boolean);
			},
			providesTags: [strapiTag],
		}),
		getSiteSetting: builder.query({
			query: () => ({ url: "/site-setting?populate[officeHours]=*" }),
			transformResponse: (response) => {
				const data = response?.data;
				const attrs = data?.attributes ?? data ?? {};

				const address = typeof attrs.address === "string" ? attrs.address : "";
				const email = typeof attrs.email === "string" ? attrs.email : "";
				const phone = typeof attrs.phone === "string" ? attrs.phone : "";
				const emergencyPhone = typeof attrs.emergencyPhone === "string" ? attrs.emergencyPhone : "";

				const officeHoursRaw = attrs.officeHours;
				const officeHours = Array.isArray(officeHoursRaw)
					? officeHoursRaw
							.map((h) => {
								const label = typeof h?.label === "string" ? h.label.trim() : "";
								const hours = typeof h?.hours === "string" ? h.hours.trim() : "";
								if (!label && !hours) return null;
								return { label, hours };
							})
							.filter(Boolean)
					: [];

				return { address, email, phone, emergencyPhone, officeHours };
			},
			providesTags: [strapiTag],
		}),
	}),
});

export const {
	useGetHomePagePartnersQuery,
	useGetHomePageAwardsQuery,
	useGetHomePageSponsorshipCommunityQuery,
	useGetTestimonialsQuery,
	useGetSiteSettingQuery,
} = strapiHomeApi;
